import {
  BoelsBillingAddress,
  BoelsGenericState,
  Cart,
  GroupOrderModification,
  Order,
  UserEnum,
} from '@boels/models';
import { Action, createReducer, on } from '@ngrx/store';
import {
  addToCart,
  addToCartFailure,
  addToCartSuccess,
  createCart,
  createCartFailure,
  createCartSuccess,
  loadCart,
  loadCartFailure,
  loadCartSuccess,
  placeOrderFail,
  placeOrderSuccess,
  removeCart,
  removeFromCart,
  removeFromCartFailure,
  removeFromCartSuccess,
  setActiveCart,
  setCheckoutAccount,
  setCheckoutStepNumber,
  setDeliveryAddress,
  setDeliveryMode,
  setPaymentInfo,
  updateCart,
  updateCartDatesSuccess,
  updateCartFailure,
  updateCartModeSuccess,
  updateCartSuccess,
} from '../actions/cart.action';

const NEWSLETTER_CONSENT_TYPE = 'Commercial';

export interface CartState extends BoelsGenericState {
  entries: { [cartId: string]: Cart };
  active: string;
  loading: boolean;
  error: boolean;
  checkoutStepNumber: number;
  errorMessage?: string;
  processCount: number;
  cartModifications: GroupOrderModification;
  billingAddress?: BoelsBillingAddress;
  order?: Order;
  checkoutAccount: UserEnum;
}

export const initialCartState: CartState = {
  entries: {},
  loading: true,
  active: '',
  error: false,
  success: false,
  errorMessage: undefined,
  processCount: 0,
  cartModifications: {},
  billingAddress: undefined,
  order: undefined,
  checkoutStepNumber: 0,
  checkoutAccount: UserEnum.ANONYMOUS,
};

const cartReducer = createReducer(
  initialCartState,

  on(loadCart, (state: CartState) => ({
    ...state,
    loading: true,
    error: false,
  })),

  on(loadCartSuccess, (state, { cart, cartId }) => ({
    ...state,
    entries: {
      ...state.entries,
      [cartId]: cart,
    },
    loading: false,
    success: true,
    error: false,
    active: cart.guid,
  })),

  on(createCart, (state) => ({
    ...state,
    loading: true,
  })),

  on(createCartSuccess, (state, { cart, cartId }) => ({
    ...state,
    entries: {
      ...state.entries,
      [cartId]: cart,
    },
    loading: false,
    success: true,
    error: false,
    active: cart.guid,
    billingAddress: undefined,
  })),

  on(createCartFailure, (state) => ({
    ...state,
    loading: false,
  })),

  on(loadCartFailure, (state, { errorMessage }) => ({
    ...state,
    active: '',
    loading: false,
    success: false,
    error: true,
    errorMessage,
  })),

  on(addToCart, (state) => ({
    ...state,
    processCount: state.processCount + 1,
    loading: true,
    error: false,
  })),

  on(addToCartSuccess, (state: CartState, { cartModifications }) => ({
    ...state,
    processCount: state.processCount - 1,
    cartModifications: cartModifications,
    error: false,
    success: true,
  })),

  on(addToCartFailure, (state, { errorMessage }) => ({
    ...state,
    processCount: state.processCount > 0 ? state.processCount - 1 : 0,
    loading: false,
    error: true,
    success: false,
    errorMessage,
  })),

  on(removeFromCart, (state) => ({
    ...state,
    loading: true,
    error: false,
  })),

  on(removeFromCartSuccess, (state) => ({
    ...state,
    success: true,
    error: false,
    loading: false,
  })),

  on(removeFromCartFailure, (state, { errorMessage }) => ({
    ...state,
    loading: false,
    error: true,
    success: false,
    errorMessage,
  })),

  on(updateCart, (state) => ({
    ...state,
    processCount: state.processCount + 1,
    loading: true,
    error: false,
  })),

  on(updateCartSuccess, (state: CartState, { cartModifications }) => ({
    ...state,
    processCount: state.processCount - 1,
    cartModifications: cartModifications,
    success: true,
    error: false,
    loading: false,
  })),

  on(updateCartFailure, (state, { errorMessage }) => ({
    ...state,
    processCount: state.processCount > 0 ? state.processCount - 1 : 0,
    loading: false,
    error: true,
    success: false,
    errorMessage,
  })),

  on(removeCart, (state, { cartId }) => ({
    ...state,
    entries: {
      ...Object.keys(state.entries)
        .filter((key) => !key.includes(cartId))
        .reduce((obj, key) => ({ ...obj, [key]: state.entries[key] }), {}),
    },
    active: '',
  })),

  on(setActiveCart, (state, { activeCartId }) => ({
    ...state,
    active: activeCartId,
    loading: false,
  })),

  on(setCheckoutStepNumber, (state, { checkoutStepNumber }) => ({
    ...state,
    checkoutStepNumber,
  })),

  on(updateCartModeSuccess, (state: CartState, { cartModifications }) => ({
    ...state,
    cartModifications: cartModifications,
    loading: false,
    error: false,
  })),

  on(placeOrderFail, (state: CartState) => ({
    ...state,
    order: undefined,
  })),

  on(placeOrderSuccess, (state: CartState, { order, newsletter }) => ({
    ...state,
    order: {
      ...order,
      newsletterConsent: !!newsletter,
      newsletterConsentType: newsletter ? NEWSLETTER_CONSENT_TYPE : '',
    },
    entries: {
      ...state.entries,
      [state.active]: {
        ...state.entries[state.active],
        totalMainUnitCount: 0,
      },
    },
  })),

  on(setDeliveryAddress, (state: CartState, { deliveryAddress }) => ({
    ...state,
    entries: {
      ...state.entries,
      [state.active]: {
        ...state.entries[state.active],
        deliveryAddress,
      },
    },
  })),
  on(setDeliveryMode, (state: CartState, { deliveryMode }) => ({
    ...state,
    entries: {
      ...state.entries,
      [state.active]: {
        ...state.entries[state.active],
        deliveryMode,
      },
    },
  })),
  on(updateCartDatesSuccess, (state: CartState, { startDate, endDate }) => ({
    ...state,
    entries: {
      ...state.entries,
      [state.active]: {
        ...state.entries[state.active],
        startDate,
        endDate,
      },
    },
  })),
  on(setPaymentInfo, (state: CartState, { paymentDetails }) => ({
    ...state,
    entries: {
      ...state.entries,
      [state.active]: {
        ...state.entries[state.active],
        paymentInfo: paymentDetails,
      },
    },
  })),
  on(setCheckoutAccount, (state: CartState, { checkoutAccount }) => ({
    ...state,
    checkoutAccount: checkoutAccount,
  })),
);

export function cartReducerFunc(state: CartState | undefined, action: Action) {
  return cartReducer(state, action);
}
